import { useState, useEffect, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  X,
  CheckCircle,
  XCircle,
  ChatCircle,
  Plus,
  Trash,
  CalendarBlank,
  WarningCircle,
  CircleNotch,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import {
  acceptAllocation,
  declineAllocation,
  requestConference,
  CONFERENCE_DEFAULTS,
  DECLINE_REASON_LABEL,
  PURPOSE_LABEL,
  FORMAT_LABEL,
  type Allocation,
  type ConferenceFormat,
  type ConferencePurpose,
  type DeclineReason,
} from '@/lib/allocationsApi';
import { checkBarristerConflict, type ConflictScanResult } from '@/lib/conflictsApi';
import { getApiErrorMessage } from '@/lib/api';
import { useMattersStore } from '@/lib/mattersStore';
import { useHearingsStore } from '@/lib/hearingsStore';
import { useDiaryStore, HOURS } from '@/lib/diaryStore';
import { useAuthStore } from '@/features/auth/store';
import { useProposedSlotClashes } from '@/lib/useBarristerBusy';
import { ProposedSlotClashWarning } from '@/components/shared/ProposedSlotClashWarning';
import { ExpandableTextarea } from '@/components/ui/expandable-textarea';
import { ScrollLock } from '@/components/shared/ScrollLock';

type Action = 'accept' | 'decline' | 'request_con';

interface Props {
  allocation: Allocation;
  initialAction: Action;
  onClose: () => void;
  onSuccess: () => void;
}

export function RespondToBriefModal({ allocation, initialAction, onClose, onSuccess }: Props) {
  const [action, setAction] = useState<Action>(initialAction);
  const queryClient = useQueryClient();
  const fetchMatters = useMattersStore((s) => s.fetchMatters);

  // Shared
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Decline-specific
  const [declineReason, setDeclineReason] = useState<DeclineReason>('diary_clash');
  const [declineOther, setDeclineOther] = useState('');

  // Conference-specific
  const [purpose, setPurpose] = useState<ConferencePurpose>('screening');
  const [layClient, setLayClient] = useState<boolean>(CONFERENCE_DEFAULTS.screening.layClientAttending);
  const [format, setFormat] = useState<ConferenceFormat>(CONFERENCE_DEFAULTS.screening.format);
  const [duration, setDuration] = useState<number>(60);
  // Proposed slots: date + selected hour (both strings, combined for API)
  const [proposedSlots, setProposedSlots] = useState<{ date: string; hour: string }[]>([{ date: '', hour: '' }]);
  // Track which slot grids are expanded (for collapsing after time selection)
  const [expandedSlots, setExpandedSlots] = useState<Record<number, boolean>>({});

  const user = useAuthStore((s) => s.user);
  // Warn (never block) if a conference slot you propose clashes your own diary.
  const proposedIso = proposedSlots
    .filter((s) => s.date && s.hour)
    .map((s) => new Date(`${s.date}T${s.hour}:00`).toISOString());
  const slotClashes = useProposedSlotClashes(user?.id ?? null, proposedIso, duration);
  const hearings = useHearingsStore((s) => s.hearings);
  const fetchHearings = useHearingsStore((s) => s.fetchHearings);
  const diaryEvents = useDiaryStore((s) => s.events);
  const fetchEvents = useDiaryStore((s) => s.fetchEvents);

  // Conflict checking state
  const [conflictWarnings, setConflictWarnings] = useState<ConflictScanResult[] | null>(null);
  const [conflictCheckPending, setConflictCheckPending] = useState(false);

  // Check for conflicts when Accept tab is selected
  useEffect(() => {
    if (action === 'accept' && !conflictWarnings && !conflictCheckPending && user?.barristerId) {
      setConflictCheckPending(true);

      // Build parties array from allocation case data
      const parties: { name: string; role: string }[] = [];
      if (allocation.case.layClient) {
        const name = allocation.case.layClient.company ??
          `${allocation.case.layClient.firstName ?? ''} ${allocation.case.layClient.lastName ?? ''}`.trim();
        if (name) parties.push({ name, role: 'lay_client' });
      }
      if (allocation.case.opponentParty) {
        parties.push({ name: allocation.case.opponentParty, role: 'opponent' });
      }

      checkBarristerConflict(user.barristerId, parties.length > 0 ? parties : undefined, allocation.caseId)
        .then(result => {
          if (result.hasConflicts) {
            setConflictWarnings(result.conflicts);
          } else {
            setConflictWarnings([]);
          }
        })
        .catch(() => {
          // Silent fail - don't block acceptance if conflict check fails
          setConflictWarnings([]);
        })
        .finally(() => setConflictCheckPending(false));
    }
  }, [action, allocation, user, conflictWarnings, conflictCheckPending]);

  // Reset conflict state when switching away from accept
  useEffect(() => {
    if (action !== 'accept') {
      setConflictWarnings(null);
    }
  }, [action]);

  const hasCriticalConflict = conflictWarnings?.some(c => c.severity === 'critical') ?? false;

  // When the conference tab opens, pull fresh hearings AND real calendar events
  // so the day grid reflects the actual diary, not seed/stale data.
  useEffect(() => {
    if (action === 'request_con') {
      fetchHearings();
      fetchEvents();
    }
  }, [action, fetchHearings, fetchEvents]);


  // For each date, build a map of hour → event for the time grid.
  // Diary events use HOURS index; hearings use HH portion of their time string.
  const getDayGrid = useMemo(() => (dateStr: string): Record<string, { label: string; sub: string; kind: string }> => {
    if (!dateStr) return {};
    const day = dateStr.slice(0, 10);
    const ln = user?.lastName ?? '';
    const fullName = user ? `${user.firstName} ${user.lastName}` : '';
    const nameVariants = [ln, fullName, `${ln} KC`, `${fullName} KC`];
    const grid: Record<string, { label: string; sub: string; kind: string }> = {};

    // Diary events: prefer UUID match (real API events carry ownerId),
    // fall back to name match for locally-created events that don't have it.
    diaryEvents
      .filter((e) => e.date === day && (
        e.ownerId === user?.id ||
        (!e.ownerId && e.barrister && nameVariants.some((n) => e.barrister?.includes(n)))
      ))
      .forEach((e) => {
        for (let s = 0; s < e.span; s++) {
          const h = HOURS[e.start + s];
          if (h) grid[h] = { label: e.title, sub: e.sub ?? '', kind: e.kind };
        }
      });

    // Hearings: match by HH prefix of the hearing time, span 1h minimum
    hearings
      .filter((h) => h.date === day && (
        h.leadBarristerUserId === user?.id || h.juniorBarristerUserId === user?.id
      ))
      .forEach((h) => {
        const hh = h.time.slice(0, 2);
        const match = HOURS.find((hr) => hr.startsWith(hh));
        if (match) grid[match] = { label: h.matterName || h.type, sub: h.court, kind: 'hearing' };
      });

    return grid;
  }, [hearings, diaryEvents, user]);

  const handlePurposeChange = (next: ConferencePurpose) => {
    setPurpose(next);
    const def = CONFERENCE_DEFAULTS[next];
    setLayClient(def.layClientAttending);
    setFormat(def.format);
  };

  const addSlot = () => {
    if (proposedSlots.length < 5) {
      const newIndex = proposedSlots.length;
      setProposedSlots([...proposedSlots, { date: '', hour: '' }]);
      setExpandedSlots((prev) => ({ ...prev, [newIndex]: true }));
    }
  };
  const removeSlot = (i: number) => {
    setProposedSlots(proposedSlots.filter((_, idx) => idx !== i));
    setExpandedSlots((prev) => {
      const next = { ...prev };
      delete next[i];
      return next;
    });
  };
  const setSlotDate = (i: number, date: string) => {
    setProposedSlots(proposedSlots.map((s, idx) => idx === i ? { ...s, date, hour: '' } : s));
    // Expand the grid when a date is selected
    setExpandedSlots((prev) => ({ ...prev, [i]: true }));
  };
  const setSlotHour = (i: number, hour: string) => {
    setProposedSlots(proposedSlots.map((s, idx) => idx === i ? { ...s, hour } : s));
    // Collapse the grid after selecting a time
    setExpandedSlots((prev) => ({ ...prev, [i]: false }));
  };
  const toggleSlotExpanded = (i: number) => {
    setExpandedSlots((prev) => ({ ...prev, [i]: !prev[i] }));
  };

  const handleSubmit = async () => {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      if (action === 'accept') {
        await acceptAllocation(allocation.id, note.trim() || undefined);
        toast.success('Brief accepted');
      } else if (action === 'decline') {
        if (declineReason === 'other' && !declineOther.trim()) {
          throw new Error('Please describe the reason in the "Other" field.');
        }
        await declineAllocation(allocation.id, {
          declineReason,
          declineReasonOther:
            declineReason === 'other' ? declineOther.trim() : undefined,
          barristerNote: note.trim() || undefined,
        });
        toast.success('Brief declined');
      } else {
        const validSlots = proposedSlots
          .filter((s) => s.date && s.hour)
          .map((s) => new Date(`${s.date}T${s.hour}:00`).toISOString());
        await requestConference(allocation.id, {
          purpose,
          layClientAttending: layClient,
          format,
          durationMinutes: duration,
          barristerNote: note.trim() || undefined,
          proposedDates: validSlots.length > 0 ? validSlots : undefined,
        });
        toast.success('Conference requested — clerk will schedule');
      }
      // Refresh every matter-related cache: Zustand store (pipeline, dashboard,
      // cases list), react-query (matter detail roles, TaskModal matter picker).
      await Promise.all([
        fetchMatters(),
        queryClient.invalidateQueries({ queryKey: ['matters'] }),
        queryClient.invalidateQueries({ queryKey: ['matter-roles'] }),
        queryClient.invalidateQueries({ queryKey: ['matter-tasks'] }),
        queryClient.invalidateQueries({ queryKey: ['allocations'] }),
        queryClient.invalidateQueries({ queryKey: ['notifications'] }),
      ]);
      onSuccess();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Could not submit response'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4">
      <ScrollLock />
      <div className="my-auto flex max-h-[88vh] w-full max-w-lg flex-col overflow-hidden rounded-lg border border-border bg-card shadow-2xl">
        {/* Header */}
        <div className="flex items-start justify-between border-b border-border px-5 py-4">
          <div className="min-w-0">
            <h2 className="truncate text-base font-semibold text-foreground">
              Respond to brief
            </h2>
            <p className="mt-0.5 truncate text-xs text-muted-foreground">
              {allocation.case.caseName} · {allocation.case.caseNumber}
            </p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Tabs */}
        <div className="grid grid-cols-3 border-b border-border bg-muted/30">
          <TabButton
            active={action === 'accept'}
            onClick={() => setAction('accept')}
            icon={<CheckCircle className="h-3.5 w-3.5" weight="fill" />}
            tone="success"
          >
            Accept
          </TabButton>
          <TabButton
            active={action === 'request_con'}
            onClick={() => setAction('request_con')}
            icon={<ChatCircle className="h-3.5 w-3.5" />}
            tone="default"
          >
            Request conference
          </TabButton>
          <TabButton
            active={action === 'decline'}
            onClick={() => setAction('decline')}
            icon={<XCircle className="h-3.5 w-3.5" />}
            tone="danger"
          >
            Decline
          </TabButton>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {action === 'accept' && (
            <div className="space-y-3">
              <p className="text-sm text-foreground">
                You&apos;re about to accept this brief. The case will be assigned to you and the
                clerk will be notified.
              </p>

              {/* Conflict check loading state */}
              {conflictCheckPending && (
                <div className="flex items-center gap-2 rounded-md border border-border bg-muted/30 px-3 py-2 text-sm text-muted-foreground">
                  <CircleNotch className="h-4 w-4 animate-spin" />
                  Checking for conflicts…
                </div>
              )}

              {/* Conflict warnings */}
              {conflictWarnings && conflictWarnings.length > 0 && (
                <div className={`rounded-md border p-3 ${
                  hasCriticalConflict
                    ? 'border-red-300 dark:border-red-500/40 bg-red-50 dark:bg-red-500/10'
                    : 'border-amber-300 dark:border-amber-500/40 bg-amber-50 dark:bg-amber-500/10'
                }`}>
                  <div className={`flex items-center gap-2 text-sm font-medium ${
                    hasCriticalConflict ? 'text-red-900 dark:text-red-200' : 'text-amber-900 dark:text-amber-200'
                  }`}>
                    <WarningCircle className="h-4 w-4" weight="fill" />
                    {hasCriticalConflict ? 'Conflict detected' : 'Potential conflict'}
                  </div>
                  <ul className={`mt-2 space-y-1 text-xs ${
                    hasCriticalConflict ? 'text-red-800 dark:text-red-300' : 'text-amber-800 dark:text-amber-300'
                  }`}>
                    {conflictWarnings.map((c, i) => (
                      <li key={i} className="flex items-start gap-1.5">
                        <span className="mt-0.5 shrink-0">•</span>
                        <span>
                          {c.reason}
                          {c.existingMatterName && (
                            <span className="ml-1 font-medium">
                              — {c.existingMatterName} ({c.existingMatterNumber})
                            </span>
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                  {hasCriticalConflict ? (
                    <p className="mt-2 text-xs font-medium text-red-700 dark:text-red-300">
                      You cannot accept this brief due to a conflict of interest.
                    </p>
                  ) : (
                    <p className="mt-2 text-xs text-amber-700 dark:text-amber-300">
                      You may still accept, but please review carefully.
                    </p>
                  )}
                </div>
              )}

              <NoteField value={note} onChange={setNote} placeholder="Optional message to the clerk (e.g. junior preferences)" />
            </div>
          )}

          {action === 'decline' && (
            <div className="space-y-3">
              <p className="text-sm text-foreground">
                Pick the reason so the clerk can decide whether to re-allocate.
              </p>
              <div>
                <label className="mb-1 block text-xs font-medium text-foreground">Reason</label>
                <div className="space-y-1.5">
                  {(['diary_clash', 'conflict_of_interest', 'other'] as DeclineReason[]).map((r) => (
                    <label
                      key={r}
                      className={`flex cursor-pointer items-start gap-2 rounded-md border px-3 py-2 text-sm transition ${
                        declineReason === r
                          ? 'border-brand-500 bg-brand-50/40'
                          : 'border-border hover:bg-muted/30'
                      }`}
                    >
                      <input
                        type="radio"
                        name="decline-reason"
                        checked={declineReason === r}
                        onChange={() => setDeclineReason(r)}
                        className="mt-0.5"
                      />
                      <span className="text-foreground">{DECLINE_REASON_LABEL[r]}</span>
                    </label>
                  ))}
                </div>
              </div>
              {declineReason === 'other' && (
                <div>
                  <label className="mb-1 block text-xs font-medium text-foreground">
                    Please describe
                  </label>
                  <ExpandableTextarea
                    label="Please describe"
                    value={declineOther}
                    onChange={setDeclineOther}
                    rows={2}
                  />
                </div>
              )}
              <NoteField value={note} onChange={setNote} placeholder="Optional message to the clerk" />
            </div>
          )}

          {action === 'request_con' && (
            <div className="space-y-4">
              <p className="text-sm text-foreground">
                Request a conference before deciding. Propose available slots below — the clerk will
                pick one, create a calendar event, and notify you.
              </p>

              {/* Conference details */}
              <div>
                <label className="mb-1 block text-xs font-medium text-foreground">Purpose</label>
                <select
                  value={purpose}
                  onChange={(e) => handlePurposeChange(e.target.value as ConferencePurpose)}
                  className="h-9 w-full rounded-md border border-border bg-card px-3 text-sm focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-100"
                >
                  {(['screening', 'case_prep', 'court_day', 'settlement', 'post_trial', 'other'] as ConferencePurpose[]).map((p) => (
                    <option key={p} value={p}>{PURPOSE_LABEL[p]}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1 block text-xs font-medium text-foreground">Lay client</label>
                  <select
                    value={layClient ? 'yes' : 'no'}
                    onChange={(e) => setLayClient(e.target.value === 'yes')}
                    className="h-9 w-full rounded-md border border-border bg-card px-3 text-sm focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-100"
                  >
                    <option value="no">No — solicitor only</option>
                    <option value="yes">Yes — lay client present</option>
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-foreground">Format</label>
                  <select
                    value={format}
                    onChange={(e) => setFormat(e.target.value as ConferenceFormat)}
                    className="h-9 w-full rounded-md border border-border bg-card px-3 text-sm focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-100"
                  >
                    {(['in_chambers', 'at_court', 'video', 'phone'] as ConferenceFormat[]).map((f) => (
                      <option key={f} value={f}>{FORMAT_LABEL[f]}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-foreground">Duration (mins)</label>
                <input
                  type="number" min={15} max={480} value={duration}
                  onChange={(e) => setDuration(Number(e.target.value))}
                  className="h-9 w-32 rounded-md border border-border bg-card px-3 text-sm focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-100"
                />
              </div>


              {/* ── Proposed slots — date picker + visual day grid ── */}
              <div>
                <label className="mb-1.5 block text-xs font-medium text-foreground">
                  Propose available slots{' '}
                  <span className="font-normal text-muted-foreground">(clerk picks one)</span>
                </label>

                <div className="space-y-4">
                  {proposedSlots.map((slot, i) => {
                    const grid = getDayGrid(slot.date);
                    const hasDate = Boolean(slot.date);
                    const isFullySet = Boolean(slot.date && slot.hour);
                    const isExpanded = expandedSlots[i] ?? false;

                    return (
                      <div key={i} className={`rounded-lg border overflow-hidden transition-all ${
                        isFullySet ? 'border-brand-500 bg-brand-50/30' : 'border-border bg-card'
                      }`}>
                        {/* ── Date row ── */}
                        <div
                          className={`flex items-center gap-2 px-3 py-2.5 ${
                            isFullySet
                              ? 'bg-brand-100/50 border-b border-brand-200'
                              : 'bg-muted/20 border-b border-border'
                          } ${hasDate ? 'cursor-pointer' : ''}`}
                          onClick={() => hasDate && toggleSlotExpanded(i)}
                        >
                          <CalendarBlank className={`h-3.5 w-3.5 shrink-0 ${isFullySet ? 'text-brand-600 dark:text-brand-400' : 'text-muted-foreground'}`} />
                          <input
                            type="date"
                            value={slot.date}
                            min={new Date().toISOString().slice(0, 10)}
                            onChange={(e) => setSlotDate(i, e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            className="h-7 flex-1 rounded border border-border bg-card px-2 text-sm focus:border-brand-600 focus:outline-none"
                          />
                          {isFullySet && (
                            <span className="flex items-center gap-1.5 rounded-full bg-brand-600 px-2 py-0.5 text-xs font-bold text-white shrink-0">
                              <CheckCircle className="h-3 w-3" weight="fill" />
                              {slot.hour}
                            </span>
                          )}
                          {hasDate && !isExpanded && !isFullySet && (
                            <span className="text-[10px] text-muted-foreground">Click to select time</span>
                          )}
                          {hasDate && (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); toggleSlotExpanded(i); }}
                              className="rounded p-1 text-muted-foreground hover:bg-muted"
                            >
                              <svg className={`h-3.5 w-3.5 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </button>
                          )}
                          {proposedSlots.length > 1 && (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); removeSlot(i); }}
                              className="rounded p-1 text-muted-foreground hover:bg-red-50 dark:bg-red-500/10 hover:text-red-600 dark:hover:text-red-400"
                            >
                              <Trash className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>

                        {/* ── Time grid — renders when a date is selected AND expanded ── */}
                        {hasDate && isExpanded && (
                          <div className="px-0 pb-1">
                            {/* Day header */}
                            <div className="flex items-center justify-between border-b border-border bg-muted/60 px-3 py-2">
                              <span className="text-xs font-bold text-foreground">
                                {new Date(slot.date + 'T12:00:00').toLocaleDateString('en-GB', {
                                  weekday: 'long', day: 'numeric', month: 'long',
                                })}
                              </span>
                              <span className="text-[10px] font-medium text-brand-700 dark:text-brand-300">
                                Tap a free slot
                              </span>
                            </div>

                            {HOURS.map((hr) => {
                              const event = grid[hr];
                              const isSelected = slot.hour === hr;
                              const isFree = !event;
                              return (
                                <button
                                  key={hr}
                                  type="button"
                                  disabled={!isFree}
                                  onClick={() => isFree && setSlotHour(i, hr)}
                                  className={`flex w-full items-stretch border-b border-border/40 text-left transition-colors last:border-0 ${
                                    isSelected
                                      ? 'bg-brand-600'
                                      : isFree
                                        ? 'hover:bg-brand-50 cursor-pointer'
                                        : 'cursor-default opacity-90'
                                  }`}
                                >
                                  {/* Time label — always black, strong */}
                                  <div className={`w-14 shrink-0 border-r px-2 py-2 text-xs font-bold ${
                                    isSelected
                                      ? 'border-brand-500 bg-brand-700 text-white'
                                      : 'border-border bg-muted/40 text-foreground'
                                  }`}>
                                    {hr}
                                  </div>

                                  {/* Slot content */}
                                  {event ? (
                                    <div className={`flex flex-1 items-center gap-2 px-3 py-2 ${
                                      event.kind === 'hearing'
                                        ? 'bg-red-50 dark:bg-red-500/10 border-l-2 border-red-500'
                                        : event.kind === 'conference'
                                          ? 'bg-amber-50 dark:bg-amber-500/10 border-l-2 border-amber-500'
                                          : 'bg-violet-50 dark:bg-violet-500/10 border-l-2 border-violet-500'
                                    }`}>
                                      <span className={`shrink-0 rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide ${
                                        event.kind === 'hearing'
                                          ? 'bg-red-100 dark:bg-red-500/15 text-red-800 dark:text-red-300'
                                          : event.kind === 'conference'
                                            ? 'bg-amber-100 dark:bg-amber-500/15 text-amber-900 dark:text-amber-200'
                                            : 'bg-violet-100 dark:bg-violet-500/15 text-violet-800 dark:text-violet-300'
                                      }`}>
                                        {event.kind === 'hearing' ? 'Hearing' :
                                         event.kind === 'conference' ? 'Conference' : 'Task'}
                                      </span>
                                      <span className="truncate text-xs font-semibold text-foreground">
                                        {event.label}
                                      </span>
                                      {event.sub && (
                                        <span className="ml-auto shrink-0 text-[10px] font-medium text-foreground/60">
                                          {event.sub}
                                        </span>
                                      )}
                                    </div>
                                  ) : (
                                    <div className={`flex flex-1 items-center px-3 py-2 ${
                                      isSelected ? '' : ''
                                    }`}>
                                      {isSelected ? (
                                        <span className="text-xs font-bold text-white">
                                          ✓ {hr} selected
                                        </span>
                                      ) : (
                                        <span className="text-xs font-medium text-foreground/70">
                                          Free — click to propose
                                        </span>
                                      )}
                                    </div>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        )}

                        {!hasDate && (
                          <div className="px-3 py-4 text-center text-xs text-muted-foreground">
                            Pick a date to see your diary for that day
                          </div>
                        )}

                        {/* Collapsed state message when time is selected */}
                        {hasDate && !isExpanded && isFullySet && (
                          <div className="px-3 py-2 text-center text-xs text-brand-700 dark:text-brand-300">
                            Click to change time
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {proposedSlots.length < 5 && (
                  <button
                    type="button"
                    onClick={addSlot}
                    className="mt-3 flex items-center gap-1 text-xs font-medium text-brand-700 dark:text-brand-300 hover:underline"
                  >
                    <Plus className="h-3 w-3" /> Add another date
                  </button>
                )}
                <div className="mt-2">
                  <ProposedSlotClashWarning clashes={slotClashes} barristerLabel="You" />
                </div>
              </div>

              <NoteField value={note} onChange={setNote} placeholder="Points to discuss, papers to bring, any other notes for the clerk…" />
            </div>
          )}
        </div>

        {error && (
          <div className="border-t border-border px-5 py-2.5">
            <div className="rounded-md border border-danger/30 bg-danger-bg px-3 py-2 text-xs text-danger">
              {error}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 border-t border-border bg-muted/30 px-5 py-3">
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || (action === 'accept' && (conflictCheckPending || hasCriticalConflict))}
            variant={action === 'decline' ? 'destructive' : 'primary'}
          >
            {submitting
              ? 'Submitting…'
              : action === 'accept'
                ? 'Confirm accept'
                : action === 'decline'
                  ? 'Submit decline'
                  : 'Request conference'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  icon,
  tone,
  children,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  tone: 'default' | 'success' | 'danger';
  children: React.ReactNode;
}) {
  const toneCls = active
    ? tone === 'success'
      ? 'border-success text-success'
      : tone === 'danger'
        ? 'border-danger text-danger'
        : 'border-brand-600 text-brand-700 dark:text-brand-300'
    : 'border-transparent text-muted-foreground hover:text-foreground';
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center justify-center gap-1.5 border-b-2 px-3 py-2.5 text-xs font-medium transition ${toneCls}`}
    >
      {icon}
      {children}
    </button>
  );
}

function NoteField({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}) {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-foreground">Note (optional)</label>
      <ExpandableTextarea
        label="Note"
        value={value}
        onChange={onChange}
        rows={2}
        placeholder={placeholder}
      />
    </div>
  );
}
